import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getAuth } from 'firebase/auth';

// ---------------------------------------------------------------------------
// Firestore Timestamp serializer
// Firestore serverTimestamp() fields arrive over the wire as
// { _seconds: number, _nanoseconds: number }.  Convert them to ISO strings
// at the network boundary so no component ever receives a plain object where
// a date string is expected.
// ---------------------------------------------------------------------------
function serializeTimestamps(value: unknown): unknown {
  if (value === null || value === undefined) return value;

  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    '_seconds' in (value as Record<string, unknown>) &&
    '_nanoseconds' in (value as Record<string, unknown>)
  ) {
    const ts = value as { _seconds: number; _nanoseconds: number };
    return new Date(
      ts._seconds * 1000 + Math.floor(ts._nanoseconds / 1_000_000),
    ).toISOString();
  }

  if (Array.isArray(value)) return value.map(serializeTimestamps);

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = serializeTimestamps(v);
    }
    return out;
  }

  return value;
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL as string,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Request interceptor — attach Firebase ID token
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const auth = getAuth();
    const user = auth.currentUser;

    // Only attach the token if the caller hasn't already provided one
    if (user && !config.headers.get('Authorization')) {
      try {
        const token = await user.getIdToken();
        config.headers.set('Authorization', `Bearer ${token}`);
      } catch {
        // Token refresh failed — let the request proceed; 401 interceptor will handle it
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — handle 401 / 402 and surface error messages
// ---------------------------------------------------------------------------
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    response.data = serializeTimestamps(response.data);
    return response;
  },
  async (error: unknown) => {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;

      if (status === 401) {
        // Sign out and redirect to login
        const auth = getAuth();
        await auth.signOut().catch(() => undefined);
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (status === 402) {
        window.location.href = '/billing';
        return Promise.reject(error);
      }

      // Extract a human-readable message from the response body
      const body = error.response.data as Record<string, unknown> | undefined;
      const message: string =
        (typeof body?.message === 'string' ? body.message : undefined) ??
        (typeof body?.error === 'string' ? body.error : undefined) ??
        error.message;

      // Attach the cleaned message so callers can display it
      error.message = message;
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Typed helper functions
// ---------------------------------------------------------------------------

export async function get<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.post<T>(url, data, config);
  return response.data;
}

export async function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.put<T>(url, data, config);
  return response.data;
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config);
  return response.data;
}

export async function del<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}

export default apiClient;
